import { Search, Calendar, Clock, User, UserPlus, AlertTriangle } from "lucide-react";
import { ReassignModal } from "../modals/ReassignModal";
import { APPOINTMENT_STATUS } from "../constants";
import { useState } from "react";

export const AppointmentList = ({
  appointments = [],
  onCheckIn,
  searchQuery = "",
  onSearchChange,
  onReassign
}) => {
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  const [showReassignModal, setShowReassignModal] = useState(false);
  const filteredAppointments = appointments.filter((appointment) => {
    const searchLower = searchQuery.toLowerCase();
    return (
      appointment.client?.fullName
        ?.toLowerCase()
        .includes(searchLower) ||
      appointment.counselor?.fullName
        ?.toLowerCase()
        .includes(searchLower)
    );
  });

  const formatTime = (time) => {
    return new Date(`2000-01-01T${time}`).toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "numeric",
      hour12: true,
    });
  };

  const handleReassign = async (appointmentId, reassignData) => {
    await onReassign(appointmentId, reassignData);
    setShowReassignModal(false);
    setSelectedAppointment(null);
  };

  return (
    <div className="bg-white dark:bg-slate-800 rounded-lg shadow border border-gray-200 dark:border-gray-700 transition-colors">
      <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
        <h2 className="text-lg font-medium text-gray-900 dark:text-white">Today's Appointments</h2>
        <div className="mt-4 relative">
          <input
            type="text"
            placeholder="Search appointments..."
            className="w-full pl-10 pr-4 py-2 border border-gray-200 dark:border-gray-700 rounded-md focus:ring-2 focus:ring-blue-500 bg-white dark:bg-slate-900 text-gray-900 dark:text-white transition-colors"
            value={searchQuery}
            onChange={(e) => onSearchChange(e.target.value)}
          />
          <Search className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" />
        </div>
      </div>

      <div className="divide-y divide-gray-200 dark:divide-gray-700">
        {filteredAppointments.length === 0 ? (
          <div className="p-6 text-center text-gray-500 dark:text-gray-400">
            No appointments found
          </div>
        ) : (
          filteredAppointments.map((appointment) => (
            <div key={appointment._id} className="p-4 hover:bg-gray-50 dark:hover:bg-slate-700/30 transition-colors">
              <div className="flex justify-between items-start">
                <div>
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-gray-900 dark:text-white">
                      {appointment.client?.fullName}
                    </span>
                    {appointment.isUrgent && (
                      <span className="px-2 py-0.5 text-xs bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300 rounded-full flex items-center gap-1">
                        <AlertTriangle className="h-3 w-3" />
                        Urgent
                      </span>
                    )}
                  </div>

                  <div className="mt-1 text-sm text-gray-500 dark:text-gray-400 space-y-1">
                    <div className="flex items-center gap-2">
                      <User className="h-4 w-4" />
                      Counselor: {appointment.counselor?.fullName}
                    </div>
                    <div className="flex items-center gap-2">
                      <Clock className="h-4 w-4" />
                      Time: {formatTime(appointment.timeSlot)}
                    </div>
                    <div className="flex items-center gap-2">
                      <Calendar className="h-4 w-4" />
                      Date: {new Date(appointment.date).toLocaleDateString()}
                    </div>
                  </div>
                </div>

                <div className="flex items-center gap-2">
                {appointment.status === APPOINTMENT_STATUS.SCHEDULED && !appointment.checkedIn && (
                  <>
                    <button
                      onClick={() => onCheckIn(appointment._id)}
                      className="px-3 py-1 text-sm bg-green-50 text-green-600 dark:bg-green-900/20 dark:text-green-400 rounded-md hover:bg-green-100 dark:hover:bg-green-900/30 transition-colors"
                    >
                      Check In
                    </button>
                    <button
                      onClick={() => {
                        setSelectedAppointment(appointment);
                        setShowReassignModal(true);
                      }}
                      className="px-3 py-1 text-sm bg-blue-50 text-blue-600 dark:bg-blue-900/20 dark:text-blue-400 rounded-md hover:bg-blue-100 dark:hover:bg-blue-900/30 flex items-center gap-1 transition-colors"
                    >
                      <UserPlus className="h-4 w-4" />
                      Reassign
                    </button>
                  </>
                )}

                  <span
                    className={`px-2 py-1 text-xs rounded-full ${
                      appointment.status === APPOINTMENT_STATUS.SCHEDULED
                        ? "bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300"
                        : appointment.status === APPOINTMENT_STATUS.COMPLETED
                        ? "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300"
                        : appointment.status === APPOINTMENT_STATUS.CANCELLED
                        ? "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300"
                        : "bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300"
                    }`}
                  >
                    {appointment.status.charAt(0).toUpperCase() +
                      appointment.status.slice(1)}
                  </span>
                </div>
              </div>

              {appointment.urgencyReason && (
                <div className="mt-2 text-sm text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/10 p-2 rounded border border-red-100 dark:border-red-900/20">
                  <strong>Urgency Reason:</strong> {appointment.urgencyReason}
                </div>
              )}
            </div>
          ))
        )}
      </div>
      <ReassignModal
        isOpen={showReassignModal}
        onClose={() => {
          setShowReassignModal(false);
          setSelectedAppointment(null);
        }}
        appointment={selectedAppointment}
        onReassign={handleReassign}
      />
    </div>
  );
};
